import { useMemo, useState } from "react";

import { Page, PageHeader } from "@/components/ui/Page";
import { EmptyState, ErrorState, Loading } from "@/components/ui/States";
import { cn } from "@/lib/cn";
import { formatAgo, today } from "@/lib/dates";
import { describeError } from "@/lib/supabase";
import { CommRow } from "./CommRow";
import {
  useCommSync,
  useCommunications,
  type Communication,
} from "./queries";

type Tab = "waiting_on_us" | "waiting_on_them";

const TABS: Array<{ key: Tab; label: string; empty: string }> = [
  {
    key: "waiting_on_us",
    label: "Needs you",
    empty: "Nobody is waiting on you.",
  },
  {
    key: "waiting_on_them",
    label: "Waiting on them",
    empty: "Nothing out with couples.",
  },
];

/**
 * Every open conversation with a client: what needs her, and what is waiting
 * on an answer from them. Closed ones and the brief are not shown here
 * (27 Oct 2026). Filled by the Claude check of info.deleeuwco@gmail.com
 * (morning and evening), later WhatsApp too. Snoozed conversations come back on their date.
 */
export function CommunicationsPage() {
  const query = useCommunications();
  const sync = useCommSync();
  const [tab, setTab] = useState<Tab>("waiting_on_us");
  const onDate = today();

  const visible = (comm: Communication) =>
    !comm.snoozed_until || comm.snoozed_until <= onDate;
  const counts = useMemo(() => {
    const all = query.data ?? [];
    return {
      waiting_on_us: all.filter(
        (comm) =>
          comm.state === "waiting_on_us" &&
          (!comm.snoozed_until || comm.snoozed_until <= onDate),
      ).length,
      waiting_on_them: all.filter((comm) => comm.state === "waiting_on_them")
        .length,
    };
  }, [query.data, onDate]);

  if (query.isPending) return <Loading label="Loading communications" />;
  if (query.isError) {
    return (
      <Page>
        <ErrorState
          title="Communications could not load"
          detail={describeError(query.error)}
          onRetry={() => void query.refetch()}
        />
      </Page>
    );
  }

  const email = sync.data?.find((row) => row.channel === "email");
  const shown = (query.data ?? []).filter(
    (comm) => comm.state === tab && (tab !== "waiting_on_us" || visible(comm)),
  );

  return (
    <Page className="max-w-[60rem]">
      <PageHeader
        eyebrow="Operations"
        title="Communications"
        meta={
          <>
            <span className={counts.waiting_on_us > 0 ? "text-amber" : ""}>
              {counts.waiting_on_us} need you
            </span>
            <span>
              {email
                ? `Email checked ${formatAgo(email.last_checked_at)}`
                : "Email not connected yet"}
            </span>
          </>
        }
      />

      <div
        role="tablist"
        aria-label="Show"
        className="mb-2 flex flex-wrap gap-1 border-b"
      >
        {TABS.map((item) => (
          <button
            key={item.key}
            role="tab"
            type="button"
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
            className={cn(
              "-mb-px border-b px-3 py-2 text-[length:var(--text-2xs)]",
              tab === item.key
                ? "border-b-accent font-semibold text-accent"
                : "border-b-transparent text-taupe hover:text-ink",
            )}
          >
            {item.label} <span className="opacity-60">{counts[item.key]}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState
          title={TABS.find((item) => item.key === tab)?.empty ?? "Nothing here"}
          body={
            email
              ? "New messages from couples appear here after each check."
              : "Once Gmail is connected, conversations with couples appear here after each check."
          }
        />
      ) : (
        <ul className="flex flex-col gap-2.5">
          {shown.map((comm) => (
            <CommRow key={comm.id} comm={comm} />
          ))}
        </ul>
      )}
    </Page>
  );
}
